import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { identifyUser } from '../lib/logrocket.js';
import VoiplyHeader from '../components/VoiplyHeader.jsx';
import VoiplyFooter from '../components/VoiplyFooter.jsx';
import ClipPlayerOverlay from '../components/ClipPlayerOverlay.jsx';

// Final screen after a customer submits. Two changes vs the prior version:
//
//   1. Confetti hero — 24 small falling pieces in 6 brand colors, plus a
//      bordered orange badge with a slowly spinning dashed ring around it.
//      Plays once on mount; honors prefers-reduced-motion via CSS so it
//      degrades to a static badge for sensitive users.
//
//   2. Receipt panel — looks like a paper printout with an orange top
//      stripe. Two sections:
//        • "Gift card going to <email>" + "Expected by <date> · in 2 business days"
//        • "What happens next" — 3-step numbered timeline with strong dollar
//          callouts ($10 per accepted, +$20 if used in marketing).
//
//   3. Your videos panel — replay strip showing the clips the customer
//      just submitted. Each clip card has a Stream poster thumbnail with a
//      play button overlay; tap to open ClipPlayerOverlay. While Stream is
//      still encoding (stream_ready=false), shows a spinner instead of the
//      play button and the card itself is disabled. Polling keeps the
//      view fresh so the play button appears the moment encoding finishes
//      without the customer having to refresh.
//
// Intent: quiet celebration without anxiety triggers. Drop the support
// number from the footer (was 'no anxiety' in the handoff notes — once a
// customer has submitted, surfacing a phone number to call is more
// stress-inducing than reassuring). Just "look for an email from Voiply,
// you can safely close this page."
//
// All dollar amounts come from the live collector — copy stays in sync if
// approval_payout_cents or usage_bonus_cents ever change.
//
// Numbers in this file:
//   • 24 = confetti pieces (one for every ~5° around the hero)
//   • 6  = confetti color count (matches .confetti.c0–c5 in CSS)
//   • 2  = business-day SLA (matches what /api/invite docs promise)
//   • 3s = polling interval while any clip is still encoding

export default function ThankYou() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [playingClip, setPlayingClip] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await api(`/api/submission/${token}`);
        setData(d);
        if (d?.submission?.customer_email) {
          identifyUser({
            email: d.submission.customer_email,
            name: d.submission.customer_name,
            role: 'customer',
          });
        }
      } catch {
        // Fail silently — the customer doesn't need this data to read the
        // page. We fall back to default copy below if data is null.
      }
    })();
  }, [token]);

  // Same polling pattern as Between: while any submitted clip is still
  // encoding on Cloudflare Stream, re-fetch every 3 seconds so the play
  // buttons turn on as soon as encoding finishes. Pauses while the tab
  // is hidden so a backgrounded phone doesn't burn requests.
  useEffect(() => {
    if (!data) return;
    const notReady = (data.clips || []).some((c) =>
      (c.status === 'uploaded' || c.status === 'approved') && !c.stream_ready
    );
    if (!notReady) return;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      if (document.visibilityState === 'hidden') return;
      try {
        const d = await api(`/api/submission/${token}`);
        if (!stopped) setData(d);
      } catch { /* ignore */ }
    };
    const id = setInterval(tick, 3000);
    return () => { stopped = true; clearInterval(id); };
  }, [data, token]);

  const approval = data?.collector
    ? (data.collector.approval_payout_cents / 100).toFixed(0)
    : '10';
  const bonus = data?.collector
    ? (data.collector.usage_bonus_cents / 100).toFixed(0)
    : '20';
  const email = data?.submission?.customer_email || '';
  const customerName = data?.submission?.customer_name || '';
  const firstName = customerName.trim().split(/\s+/)[0] || 'there';

  // Estimated arrival = today + 2 business days. Skip weekends — boomers
  // notice when "in 2 business days" lands on a Saturday and lose trust.
  // Build inside render so the date is always fresh; this page is short-
  // lived anyway (customer reads it, closes the tab).
  const today = new Date();
  const arrival = new Date(today);
  let added = 0;
  while (added < 2) {
    arrival.setDate(arrival.getDate() + 1);
    const dow = arrival.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  const fmtDate = (d) =>
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="vt-app min-h-screen flex flex-col">
      <VoiplyHeader />
      <main className="vt-thanks-page flex-1 w-full max-w-[720px] mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Hero: confetti + badge + headline */}
        <div className="vt-thanks-hero animate-fade-in-up">
          <div className="vt-thanks-confetti" aria-hidden="true">
            {/*
              24 pieces, 6 brand colors (mod 6). Each gets a --vt-i custom
              property used by the CSS animation-delay so the fall is
              staggered, not synchronized. CSS handles the rest of the
              motion + the prefers-reduced-motion override.
            */}
            {Array.from({ length: 24 }).map((_, i) => (
              <span
                key={i}
                className={`vt-confetti vt-confetti-c${i % 6}`}
                style={{ ['--vt-i']: i }}
              />
            ))}
          </div>
          <div className="vt-thanks-badge animate-scale-in">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 className="vt-thanks-headline">Sent! Thank you, {firstName}.</h1>
          <p className="vt-thanks-lede">
            Your videos are on their way to our team. We've got it from here.
          </p>
        </div>

        {/* Receipt panel: what's coming, where, when, and the timeline */}
        <div className="vt-thanks-receipt animate-fade-in-up" style={{ animationDelay: '120ms' }}>
          <div className="vt-receipt-strip" aria-hidden="true"></div>

          <div className="vt-receipt-section">
            <div className="vt-receipt-row">
              <span className="vt-receipt-label">Gift card going to</span>
              <span className="vt-receipt-value">{email || 'the email on file'}</span>
            </div>
            <div className="vt-receipt-row">
              <span className="vt-receipt-label">Expected by</span>
              <span className="vt-receipt-value vt-receipt-value-with-pill">
                {fmtDate(arrival)}
                <span className="vt-pill vt-pill-orange">in 2 business days</span>
              </span>
            </div>
          </div>

          <div className="vt-receipt-divider" aria-hidden="true"></div>

          <div className="vt-receipt-section">
            <h2 className="vt-receipt-section-title">What happens next</h2>
            <div className="vt-thanks-timeline">
              <div className="vt-thanks-step">
                <span className="vt-thanks-step-num">1</span>
                <div className="vt-thanks-step-body">
                  <div className="vt-thanks-step-title">We watch each video</div>
                  <div className="vt-thanks-step-sub">
                    A Voiply teammate reviews them within 2 business days.
                  </div>
                </div>
              </div>
              <div className="vt-thanks-step">
                <span className="vt-thanks-step-num">2</span>
                <div className="vt-thanks-step-body">
                  <div className="vt-thanks-step-title">Gift card lands in your inbox</div>
                  <div className="vt-thanks-step-sub">
                    <strong>${approval}</strong> for each video we accept. Pick Amazon, Visa, Target, and more.
                  </div>
                </div>
              </div>
              <div className="vt-thanks-step">
                <span className="vt-thanks-step-num">3</span>
                <div className="vt-thanks-step-body">
                  <div className="vt-thanks-step-title">Bonus if we use one in an ad</div>
                  <div className="vt-thanks-step-sub">
                    Another <strong>${bonus}</strong> per video featured. One bonus per video, no cap.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Your videos — replay panel. Renders only when the submission
            actually has clips (data fetched & non-empty). Each clip card
            shows its real Stream poster + a play button overlay; tapping
            opens ClipPlayerOverlay. While Stream is still encoding the
            card shows a spinner instead. */}
        {data?.clips?.length > 0 && (
          <div className="vt-thanks-videos animate-fade-in-up" style={{ animationDelay: '180ms' }}>
            <h2 className="vt-thanks-videos-title">Your videos</h2>
            <p className="vt-thanks-videos-sub">
              Rewatch what you sent. Closing this page won't affect your gift card.
            </p>
            <div className="vt-thanks-videos-grid">
              {data.clips.map((c) => {
                const script = (data.collector?.scripts || []).find((s) => s.key === c.script_key);
                const canPlay = !!c.iframe_url;
                const dur = c.duration_seconds && c.duration_seconds > 0
                  ? `${Math.floor(c.duration_seconds / 60)}:${String(Math.round(c.duration_seconds % 60)).padStart(2, '0')}`
                  : null;
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={'vt-thanks-video-card' + (canPlay ? ' vt-thanks-video-card-playable' : '')}
                    onClick={() => canPlay && setPlayingClip(c)}
                    disabled={!canPlay}
                    aria-label={canPlay ? `Play your ${script?.title || 'video'}` : 'Video still processing'}
                  >
                    <div className="vt-thanks-video-thumb">
                      {c.thumbnail_url ? (
                        <img
                          className="vt-thanks-video-poster"
                          src={c.thumbnail_url}
                          alt=""
                          loading="lazy"
                          /* If the poster 404s (rare but Stream's CDN can lag
                             a few seconds even after stream_ready flips
                             true), hide the broken-image icon and let the
                             placeholder gradient show through. The next poll
                             tick will retry with a fresh src. */
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="vt-thanks-video-poster-placeholder" />
                      )}
                      <div className="vt-thanks-video-play">
                        {canPlay ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M7 4l13 8-13 8z"/>
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 50 50" aria-hidden="true">
                            <circle cx="25" cy="25" r="20" fill="none" stroke="currentColor" strokeWidth="4" strokeOpacity=".25"/>
                            <path fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" d="M25 5 a20 20 0 0 1 20 20">
                              <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite"/>
                            </path>
                          </svg>
                        )}
                      </div>
                      {dur && <span className="vt-thanks-video-time">{dur}</span>}
                    </div>
                    <div className="vt-thanks-video-meta">
                      <span className="vt-thanks-video-title">
                        {script?.title || 'Your video'}
                      </span>
                      <span className="vt-thanks-video-status">
                        {canPlay ? 'Tap to replay' : 'Processing…'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Quiet footer — no support number, no anxiety. The customer is done. */}
        <p className="vt-thanks-foot animate-fade-in-up" style={{ animationDelay: '240ms' }}>
          Look for an email from <strong>Voiply</strong>. You can safely close this page.
        </p>
      </main>
      <VoiplyFooter />

      {playingClip && (
        <ClipPlayerOverlay
          clip={playingClip}
          script={(data?.collector?.scripts || []).find((s) => s.key === playingClip.script_key)}
          onClose={() => setPlayingClip(null)}
        />
      )}
    </div>
  );
}
